import { EyeIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { DownloadIcon, FileIcon, HelpCircle, Trash } from "../../../assets/icons/interface/interface";
import { Alert } from "../../../components/Alert/Alert";
import { Badge } from "../../../components/Badge/Badge";
import { Label } from "../../../components/Label/Label";
import Spinner from "../../../components/Spinner";
import { Button } from "../../../components/ui/button";
import { buildDocumentUrl, sanitizeFileName } from "../../../handlers/documentStorage";
import { docSubTypes, dspDocTypes } from "../../../handlers/enums";
import { getErrorMessage, isConflictError } from "../../../handlers/errorHandling";
import { generateFileHash } from "../../../handlers/utilityFunctions";
import { useDeleteDocument, useSmartProfile, useUpdateSmartProfile } from "../../../services/create-resume/queries";
import FileUploadService from "../../../services/fileUploadService";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../JobsPage/components/Select";
import FileUpload from "./FileUpload";
import { Modal } from "./Modal";

const initialErrorsState: { documentType: string | null; file: string | null } = {
	documentType: null,
	file: null,
};

// Server-side limits from CreateDspDto. Truncate to them here so the user never gets a
// class-validator 400 after the request round-trips.
const MAX_TITLE_LENGTH = 50;
const MAX_DESCRIPTION_LENGTH = 140;

export type SmartProfileDoc = {
	doc_id?: string;
	doc_type?: string;
	doc_sub_type?: string;
	file_name?: string;
	file_hash?: string;
	storage_key?: string;
	doc_description?: string;
};

export type SmartProfileDsp = {
	dsp_id?: string;
	dsp_title?: string;
	source_resume?: SmartProfileDoc;
	dsp_docs?: SmartProfileDoc[];
};

export type SmartProfileData = {
	dsps?: SmartProfileDsp[];
	general_docs?: SmartProfileDoc[];
};

type UploadInfo = { uploadUrl?: string; content_type?: string };
type UploadResponse = UploadInfo & { upload_info?: UploadInfo };

type DeleteDocumentPayload = {
	doc_id: string;
	doc_type?: string;
	dspId?: string;
};

type UploadPayload = {
	dspId: string;
	dsp_title?: string;
	doc_description: string;
	dsp_description: string;
	dsp_doc_type: string;
	doc_sub_type: string;
	file_name?: string;
	file_hash: string;
};

const EMPTY_SMART_PROFILE: SmartProfileData = { dsps: [], general_docs: [] };

// PATCH /smart-profiles/:dspId nests the presign under `upload_info` for a Resume but
// returns it bare for a General Doc or DSP Doc. Accept either shape.
function resolveUploadInfo(response: UploadResponse | undefined): UploadInfo {
	return response?.upload_info ?? response ?? {};
}

type DocumentCenterProps = {
	selectedDSPId?: string | null;
	setShowDocument?: (value: boolean) => void;
	setDocumentName?: (value: string | null) => void;
	setDocumentUrl?: (value: unknown) => void;
	setLoadingDocument?: (value: boolean) => void;
	profileView?: boolean;
};

export default function DocumentCenter({
	selectedDSPId,
	setShowDocument,
	setDocumentName,
	setDocumentUrl,
	setLoadingDocument,
	profileView = true,
}: DocumentCenterProps) {
	const [loading, setLoading] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [showReplaceResumeModal, setShowReplaceResumeModal] = useState(false);
	const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
	const [errors, setErrors] = useState(initialErrorsState);
	const [deleteDoc, setDeleteDoc] = useState<SmartProfileDoc | null>(null);
	const [showUploadSection, setShowUploadSection] = useState(false);
	const [documents, setDocuments] = useState<SmartProfileDoc[]>([]);
	const [formData, setFormData] = useState<{ file: File | null; documentType: string }>({
		file: null,
		documentType: "",
	});
	const uploadSectionRef = useRef<HTMLDivElement | null>(null);

	const {
		data: smartProfileData,
		isLoading: isLoadingSmartProfileData,
		refetch: refetchSmartProfile,
	} = useSmartProfile();

	// The query is undefined while loading, undefined when disabled without an auth
	// cookie, and empty for a user with no Smart Profile. Normalise all three so no
	// consumer below has to guard.
	const smartProfile: SmartProfileData = useMemo(() => {
		if (isLoadingSmartProfileData || !smartProfileData) return EMPTY_SMART_PROFILE;
		return smartProfileData as SmartProfileData;
	}, [smartProfileData, isLoadingSmartProfileData]);

	const dsps = useMemo(() => smartProfile.dsps ?? [], [smartProfile]);

	// Prefer the DSP the user is looking at, and fall back to their first one.
	const targetDsp = useMemo(
		() => dsps.find((dsp) => dsp.dsp_id === selectedDSPId) ?? dsps[0] ?? null,
		[dsps, selectedDSPId],
	);

	// Whose documents the list below is actually built from — no `dsps[0]` fallback, because until
	// DSPItem reports a selection there is no DSP in the list and only general documents show. The
	// heading is written off this so the sentence cannot describe a DSP the list is not showing.
	const listedDsp = useMemo(() => dsps.find((dsp) => dsp.dsp_id === selectedDSPId) ?? null, [dsps, selectedDSPId]);

	const deleteMutation = useDeleteDocument({
		onSuccess: () => {
			setSelectedDocId(null);
			toast.success("Document deleted successfully.");
		},
		onError: (error: unknown) => {
			setSelectedDocId(null);
			toast.error(getErrorMessage(error, "Could not delete the document. Please try again."));
		},
	});

	// The query hooks live in untyped JS, so react-query infers a void payload.
	// Narrow them here rather than casting at every call site.
	const deleteDocumentMutation = deleteMutation.mutate as unknown as (payload: DeleteDocumentPayload) => void;
	const deleting: boolean = deleteMutation.isPending;

	const updateSmartProfileAsync = useUpdateSmartProfile().mutateAsync as unknown as (
		payload: UploadPayload,
	) => Promise<UploadResponse>;
	const deleteDocumentAsync = useDeleteDocument().mutateAsync as unknown as (
		payload: DeleteDocumentPayload,
	) => Promise<unknown>;

	const findOwningDspId = (doc: SmartProfileDoc) =>
		dsps.find((dsp) => (dsp.dsp_docs ?? []).some((dspDoc) => dspDoc.doc_id === doc.doc_id))?.dsp_id;

	// The delete endpoint rejects a Resume: a Smart Profile always keeps one source
	// resume, and a new upload replaces it. Offer that path instead of a dead action.
	const isResumeDoc = (doc: SmartProfileDoc) =>
		doc.doc_type === dspDocTypes.RESUME || doc.doc_sub_type === docSubTypes.RESUME.name;

	function startResumeReplacement() {
		setShowReplaceResumeModal(false);
		setErrors(initialErrorsState);
		setFormData({ file: null, documentType: docSubTypes.RESUME.name });
		setShowUploadSection(true);
		uploadSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
	}

	const deleteDocument = () => {
		if (!deleteDoc?.doc_id) {
			setShowDeleteModal(false);
			return;
		}

		setSelectedDocId(deleteDoc.doc_id);
		deleteDocumentMutation({
			doc_id: deleteDoc.doc_id,
			doc_type: deleteDoc.doc_type,
			dspId: findOwningDspId(deleteDoc) ?? targetDsp?.dsp_id,
		});
		setShowDeleteModal(false);
	};

	// The API writes the document row before the file reaches S3, and rejects a
	// duplicate file_hash. A failed upload therefore leaves a row that blocks every
	// later attempt with a 409. Remove it through the existing delete endpoint, which
	// only pulls the Mongo row and never touches S3 — exactly right for a row whose
	// object was never created.
	async function removeDocumentByFileHash(fileHash: string): Promise<boolean> {
		if (!fileHash) return false;

		const { data } = await refetchSmartProfile();
		const profile = (data ?? EMPTY_SMART_PROFILE) as SmartProfileData;

		const generalDoc = (profile.general_docs ?? []).find((doc) => doc.file_hash === fileHash);

		if (generalDoc?.doc_id) {
			await deleteDocumentAsync({ doc_id: generalDoc.doc_id, doc_type: dspDocTypes.GENERAL_DOC });
			return true;
		}

		for (const dsp of profile.dsps ?? []) {
			const dspDoc = (dsp.dsp_docs ?? []).find((doc) => doc.file_hash === fileHash);

			if (dspDoc?.doc_id) {
				await deleteDocumentAsync({ doc_id: dspDoc.doc_id, doc_type: dspDocTypes.DSP_DOC, dspId: dsp.dsp_id });
				return true;
			}
		}

		// A matching source resume is left alone on purpose: the delete endpoint rejects
		// Resume, and silently removing someone's resume is not a safe retry.
		return false;
	}

	async function requestUploadAndSend(payload: UploadPayload, file: File) {
		const response = (await updateSmartProfileAsync(payload)) as UploadResponse;
		const { uploadUrl, content_type: contentType } = resolveUploadInfo(response);

		if (!uploadUrl) {
			throw new Error("The server did not return an upload link. Please try again.");
		}

		await new Promise<void>((resolve, reject) => {
			FileUploadService.uploadResumeFileToBucketWithFetchAPI(
				{ uploadUrl, file, contentType },
				() => resolve(),
				(error) => reject(error),
			);
		});
	}

	async function uploadDocument(payload: UploadPayload, file: File) {
		try {
			await requestUploadAndSend(payload, file);
			return;
		} catch (error) {
			// Drop whatever row this attempt left behind so the hash is free again.
			const removed = await removeDocumentByFileHash(payload.file_hash).catch(() => false);

			// A 409 means an earlier failed upload had already claimed this hash. It is
			// gone now, so the same submission can go through without asking the user.
			if (removed && isConflictError(error)) {
				await requestUploadAndSend(payload, file);
				return;
			}

			throw error;
		}
	}

	function handleSubmit() {
		// Reset errors
		setErrors(initialErrorsState);
		let formHasErrors = false;

		//Form Validation
		if (formData.documentType === "") {
			formHasErrors = true;
			setErrors((prevState) => {
				return {
					...prevState,
					documentType: "Please Select a document type.",
				};
			});
		}

		if (formData.file === null) {
			formHasErrors = true;
			setErrors((prevState) => {
				return {
					...prevState,
					file: "Please upload a document file.",
				};
			});
		}

		if (formHasErrors) return;

		const file = formData.file;

		if (!file) return;

		if (!targetDsp?.dsp_id) {
			toast.error("Create a Smart Profile before you upload documents.");
			return;
		}

		const dspId = targetDsp.dsp_id;

		//Submitting form data to the API
		setLoading(true);

		// The description field is hidden: the file name describes the document well enough,
		// and doc_description is capped at 140 characters server-side.
		const description = (file.name ?? "").slice(0, MAX_DESCRIPTION_LENGTH);

		generateFileHash(file)
			.then(async (fileHash: string) => {
				const payload: UploadPayload = {
					dspId,
					// dsp_title is capped at 50 characters server-side, and file names exceed that.
					dsp_title: file.name?.slice(0, MAX_TITLE_LENGTH),
					// The API stores the description from doc_description; dsp_description alone
					// is accepted but silently discarded. Send both.
					doc_description: description,
					dsp_description: description,
					dsp_doc_type: getDspDocType(formData.documentType),
					doc_sub_type: formData.documentType,
					// Send an already-normalised name so the stored storage_key matches the
					// object the API uploads to. See sanitizeFileName.
					file_name: sanitizeFileName(file.name),
					file_hash: fileHash,
				};

				await uploadDocument(payload, file);

				setFormData({ file: null, documentType: "" });
				toast.success("Document uploaded successfully");
			})
			.catch((error: unknown) => {
				toast.error(getErrorMessage(error, "Could not upload the document. Please try again."));
			})
			.finally(() => {
				setLoading(false);
			});
	}

	function getDspDocType(documentType) {
		let docType;

		switch (documentType) {
			case docSubTypes.RESUME.name:
				docType = dspDocTypes.RESUME;
				break;
			case docSubTypes.META_DATA.name:
				docType = dspDocTypes.META_DATA;
				break;
			case docSubTypes.CERTIFICATE.name:
			case docSubTypes.CREDENTIAL.name:
			case docSubTypes.PORTFOLIO.name:
				docType = dspDocTypes.GENERAL_DOC;
				break;
			case docSubTypes.COVER_LETTER.name:
				docType = dspDocTypes.DSP_DOC;
				break;
			default:
				docType = dspDocTypes.DSP_DOC;
		}

		return docType;
	}

	useEffect(() => {
		const generalDocs = smartProfile.general_docs ?? [];

		if (!listedDsp) {
			setShowUploadSection(generalDocs.length === 0);
			setDocuments(generalDocs);
			return;
		}

		// Spreading an absent source_resume produced a blank, undeletable row.
		const sourceResume = listedDsp.source_resume?.doc_id ? [listedDsp.source_resume] : [];
		const nextDocuments = [...sourceResume, ...generalDocs, ...(listedDsp.dsp_docs ?? [])];

		setShowUploadSection(nextDocuments.length === 0);
		setDocuments(nextDocuments);
	}, [smartProfile, listedDsp]);

	return (
		// The profile page already wraps this in a padded card, so a top margin here reads as
		// dead space above the heading. Only the tabbed view, which has no heading, needs it.
		<div className={`w-full ${profileView ? "" : "mt-8"}`}>
			{profileView && (
				<div className="flex justify-between items-center gap-4">
					<div className="">
						<h3 className="text-lg font-bold font-plus-jakarta text-gray-800">Documents</h3>
						<p className="text-sm text-gray-700 font-normal font-inter mb-4">
							{/* Titles are cut from an uploaded file name, so some carry trailing whitespace. */}
							{listedDsp?.dsp_title?.trim()
								? `Your general documents, and the documents attached to ${listedDsp.dsp_title.trim()}.`
								: "Your general documents."}
						</p>
					</div>
					<Button
						variant="ghost"
						className="text-[#0B6BCF] font-medium pr-2 shrink-0"
						onClick={() => setShowUploadSection(!showUploadSection)}
					>
						{showUploadSection ? "Close" : "Add Document"}
					</Button>
				</div>
			)}

			<div
				ref={uploadSectionRef}
				className={`upload-section max-h-0 overflow-hidden transition-all duration-700 ease-in-out ${showUploadSection ? "max-h-screen" : ""}`}
			>
				<div className="mb-6 pl-1 pr-1">
					<h3 className="text-lg font-bold mb-4 font-plus-jakarta text-gray-800">Upload New Document</h3>
					<div className="space-y-4">
						<div>
							<Label htmlFor="document-type" className="text-sm font-medium text-gray-700 mb-1.5 block">
								Document Type
							</Label>
							<Select
								value={formData.documentType}
								onValueChange={(val) => {
									setFormData((prev) => {
										return { ...prev, documentType: val };
									});
								}}
							>
								<SelectTrigger id="document-type" className="h-[36px]">
									<SelectValue placeholder="Select document type" />
								</SelectTrigger>
								<SelectContent>
									{Object.values(docSubTypes)
										.map((item) => item.name)
										.map((type) => (
											<SelectItem key={type} value={type}>
												{type}
											</SelectItem>
										))}
								</SelectContent>
							</Select>
							<div className="text-red-500">{errors.documentType}</div>
						</div>
						<FileUpload
							processDataOfUploadedFile={(file) => {
								setFormData((prev) => {
									return { ...prev, file: file as File };
								});
							}}
							removeFile={() => {
								setFormData((prev) => {
									return { ...prev, file: null };
								});
							}}
							fileName={formData?.file?.name || ""}
							fileError={errors.file}
							setFileError={(error) => setErrors({ ...errors, file: error })}
							multiple={false}
							// .doc is deliberately absent: the API only presigns pdf, txt and docx,
							// and it rejects anything else only after the document row is written.
							supportedFiles={[
								{ name: "PDF", extension: ".pdf", mimeType: "application/pdf" },
								{ name: "TXT", extension: ".txt", mimeType: "text/plain" },
								{
									name: "DOCX",
									extension: ".docx",
									mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
								},
							]}
							disableFileInput={false}
						/>
					</div>
					<div className="flex justify-end mt-4">
						<Button className="whitespace-nowrap" onClick={handleSubmit}>
							{loading ? <>Submitting...</> : "Submit"}
						</Button>
					</div>
				</div>

				{/* Divider */}
				<div className="border-t border-gray-200 mb-6" />
			</div>

			{/* Documents List */}
			<div className="pr-1">
				<div className="space-y-2">
					{documents.length === 0 ? (
						<Alert className="flex items-center" icon={<HelpCircle />}>
							<div className="space-y-1">
								<h2 className="text-lg font-semibold tracking-normal text-gray-900 mt-1 mb-2">No documents</h2>
								<p className="text-gray-800 text-sm mb-8">
									You haven&apos;t uploaded any documents yet. Use the form above to add your first document.
								</p>
							</div>
						</Alert>
					) : (
						documents.map((doc) => (
							<div
								key={doc.doc_id}
								className="flex items-center flex-wrap justify-between p-3.5 bg-gray-50 rounded-lg transition-colors hover:bg-gray-100"
							>
								{/* An uploaded file name has no spaces to wrap on, so a long one used to push this
								    row past the viewport and scroll the whole page sideways. basis-full takes a line
								    of its own on a phone — letting the actions wrap below — rather than being squeezed
								    to a single character per line beside them. */}
								<div className="flex items-center gap-3 min-w-0 basis-full sm:basis-0 sm:flex-1">
									<div className="p-2 bg-primary-50 rounded-md shrink-0">
										<FileIcon className="w-4 h-4 text-primary-700" />
									</div>
									<div className="min-w-0">
										<h4 className="text-[14px] font-medium text-gray-900 font-inter mb-0.5 break-words">
											{doc.file_name}
										</h4>
									</div>
								</div>

								<div className="flex items-center gap-6">
									<Badge variant="secondary" className="text-xs font-medium bg-gray-100 text-gray-700">
										{doc.doc_sub_type}
									</Badge>
									<div className="flex items-center gap-2">
										<Button
											variant="ghost"
											size="sm"
											title="Download"
											className="h-8 w-8 p-0 hover:bg-gray-100"
											onClick={() => {
												const documentUrl = buildDocumentUrl(doc.storage_key);
												setLoadingDocument && setLoadingDocument(true);
												setDocumentUrl &&
													setDocumentUrl((prev) => {
														if (prev === documentUrl) {
															setDocumentUrl && setDocumentUrl(null);
															setDocumentName && setDocumentName(null);
															setShowDocument && setShowDocument(false);
														} else {
															setDocumentName && setDocumentName(doc.file_name);
															setShowDocument && setShowDocument(true);
														}

														return documentUrl;
													});
											}}
										>
											<EyeIcon className="w-4 h-4 text-gray-600" />
											<span className="sr-only">View</span>
										</Button>
										<Button
											variant="ghost"
											size="sm"
											title="Download"
											className="h-8 w-8 p-0 hover:bg-gray-100"
											onClick={() => {
												const downloadUrl = buildDocumentUrl(doc.storage_key);
												if (!downloadUrl) return;

												const link = document.createElement("a");
												link.href = downloadUrl;
												link.download = doc.file_name || "document";
												link.target = "_blank";
												document.body.appendChild(link);
												link.click();
												document.body.removeChild(link);
											}}
										>
											<DownloadIcon className="w-4 h-4 text-gray-600" />
											<span className="sr-only">Download</span>
										</Button>
										{deleting && selectedDocId === doc.doc_id && (
											<div className="ignore-focus-behavior inline-flex items-center justify-center focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background rounded-md h-8 w-8 p-0">
												<Spinner />
											</div>
										)}
										{(!deleting || (deleting && selectedDocId !== doc.doc_id)) && (
											<Button
												variant="ghost"
												size="sm"
												title="Delete"
												disabled={deleting && selectedDocId !== doc.doc_id}
												className="h-8 w-8 p-0 hover:bg-red-50 hover:text-red-600"
												onClick={() => {
													if (isResumeDoc(doc)) {
														setShowReplaceResumeModal(true);
														return;
													}

													setDeleteDoc(doc);
													setShowDeleteModal(true);
												}}
											>
												<Trash className="w-4 h-4" />
												<span className="sr-only">Delete</span>
											</Button>
										)}
									</div>
								</div>
							</div>
						))
					)}
				</div>
			</div>

			{/* The profile view puts this action in the heading row instead — one trigger, not two. */}
			{!profileView && (
				<div className="flex justify-end mt-4">
					<Button size="sm" className="px-7" onClick={() => setShowUploadSection(!showUploadSection)}>
						{showUploadSection ? "Close" : "Add"}
					</Button>
				</div>
			)}

			<Modal
				isOpen={showDeleteModal}
				onClose={() => setShowDeleteModal(false)}
				title="Delete document confirmation"
				secondaryAction={{
					label: "Cancel",
					onClick: () => setShowDeleteModal(false),
				}}
				primaryAction={{
					label: "Delete",
					onClick: () => deleteDocument(),
				}}
			>
				<div className="overflow-y-auto">
					<div className="">
						<div className="pb-3 max-h-[calc(100vh-70px)] overflow-y-auto pr-2">
							<div className="space-y-3 mb-3">Are you Sure you want to delete this document?</div>
						</div>
					</div>
				</div>
			</Modal>

			<Modal
				isOpen={showReplaceResumeModal}
				onClose={() => setShowReplaceResumeModal(false)}
				title="You cannot delete a resume"
				secondaryAction={{
					label: "Cancel",
					onClick: () => setShowReplaceResumeModal(false),
				}}
				primaryAction={{
					label: "Upload new resume",
					onClick: () => startResumeReplacement(),
				}}
			>
				<div className="pb-3">
					<p className="text-sm text-gray-700">
						Your Smart Profile keeps one resume at a time. Upload a new resume and we replace this one for you.
					</p>
				</div>
			</Modal>
		</div>
	);
}
